import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import TextField from 'core/components/validatedForm/TextField'
import Dropdown from 'core/elements/dropdown/Dropdown'
import clsx from 'clsx'
import { FieldValidator } from 'core/utils/fieldValidators'
import { InputProps } from 'core/elements/input/Input'

export type Unit = 'MiB' | 'GiB' | 'TiB'

export interface SizeInputWithUnit extends InputProps {
  id?: string
  label?: string
  value?: number | null
  defaultUnit?: Unit
  outputValueUnit?: Unit
  disabled?: boolean
  min?: number
  max?: number
  onChange?: (convertedValue: number | null) => void
  className?: string
  validations?: FieldValidator[]
  resetValueToZero?: boolean
  items?: { label: string; value: Unit }[]
}

const UNITS: Unit[] = ['MiB', 'GiB', 'TiB']

const UNIT_TO_MIB = {
  MiB: 1,
  GiB: 1024,
  TiB: 1024 * 1024,
}

function convert(value: number, from: Unit, to: Unit) {
  if (!Number.isFinite(value)) return null
  const inMiB = value * UNIT_TO_MIB[from]
  return inMiB / UNIT_TO_MIB[to]
}

const defaultItems = UNITS.map((unit) => ({ label: unit, value: unit }))

const SizeInputWithUnit: React.FC<SizeInputWithUnit> = ({
  id,
  min,
  max,
  label,
  value,
  disabled,
  className,
  validations,
  defaultUnit = 'GiB',
  outputValueUnit = 'MiB',
  onChange,
  items,
  resetValueToZero = false,
  ...rest
}) => {
  const classes = useStyles()
  const [unit, setUnit] = useState(defaultUnit)
  const [rawInput, setRawInput] = useState('')

  const isUnlimited = value === -1

  // Derived display value from props
  const displayValue = useMemo(() => {
    if (resetValueToZero) return '0'
    if (rawInput !== '') return rawInput
    if (value == null) return ''
    if (isUnlimited) return '-1'
    const converted = convert(value, outputValueUnit, unit)
    return converted === null ? '' : String(converted)
  }, [rawInput, value, isUnlimited, outputValueUnit, unit])

  const handleNumberChange = useCallback(
    (val) => {
      setRawInput(val)
      if (val === '' || val === undefined || val === null) {
        onChange && onChange(null)
        return
      }
      if (val === '-1') {
        onChange && onChange(-1)
        return
      }
      if (onChange) {
        const converted = convert(Number(val), unit, outputValueUnit)
        onChange(converted)
      }
    },
    [onChange, outputValueUnit, unit],
  )

  const handleUnitChange = useCallback(
    (newUnit: Unit) => {
      setUnit(newUnit)
      setRawInput('')
    },
    [unit],
  )

  return (
    <div className={clsx(classes.root, className)}>
      <TextField
        id={id}
        label={label}
        disabled={disabled}
        onChange={handleNumberChange}
        value={displayValue}
        type="number"
        validations={validations}
        min={min}
        max={max}
        {...rest}
        endAdornment={
          <div className={classes.unitDropdown}>
            <Dropdown
              id={`${id}-unit`}
              items={items || defaultItems}
              value={unit}
              onChange={handleUnitChange}
              disabled={disabled || isUnlimited}
              width={120}
              placeholder="Unit"
            />
          </div>
        }
      />
    </div>
  )
}

export default SizeInputWithUnit

const useStyles = makeStyles<Theme>(() => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 8,
    alignItems: 'end',
  },
  unitDropdown: {
    '& > div': {
      '& .inputFrame': {
        borderRadius: 0,
        borderRight: 'none',
        borderTop: 'none',
        borderBottom: 'none',
        backgroundColor: 'unset',
      },
    },
  },
}))
